// src/pages/ProfilePage.js
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/Navbar";
import PersonalInfo from "./PersonalInfo";
import LanguagePicker from "./LanguagePicker";
import AvatarPicker from "./AvatarPicker";
import { api } from "../../api";
import "./ProfilePage.css";

import avatarMale from "../../assets/avatars/avatar_male.png";
import avatarFemale from "../../assets/avatars/avatar_female.png";

import { FiEdit2 } from "react-icons/fi";

function loadAvatarMap() {
  const req = require.context("../../assets/avatars", false, /\.(png|jpe?g|svg|webp)$/i);
  const map = {};
  req.keys().forEach((p) => {
    const mod = req(p);
    const src = mod?.default ?? mod;
    const filename = p.replace("./", "");
    const key = filename.replace(/\.[^.]+$/, "");
    map[key] = src;
  });
  return map;
}

export default function ProfilePage({ user, onLogout, onUpdateProfile }) {
  const { t } = useTranslation();

  const profile = user?.profile || {};
  const sex = (profile.sex || "").toLowerCase();

  const fallbackAvatarSrc = sex === "female" ? avatarFemale : avatarMale;
  const avatarMap = useMemo(() => loadAvatarMap(), []);

  const [avatarKey, setAvatarKey] = useState(profile.avatarKey || "");
  const [language, setLanguage] = useState(profile.language || "en");

  useEffect(() => {
    setAvatarKey(profile.avatarKey || "");
  }, [profile.avatarKey]);

  useEffect(() => {
    setLanguage(profile.language || "en");
  }, [profile.language]);

  const [editingAvatar, setEditingAvatar] = useState(false);
  const [draftAvatarKey, setDraftAvatarKey] = useState(avatarKey);

  const headerAvatarSrc =
    avatarKey && avatarMap[avatarKey] ? avatarMap[avatarKey] : fallbackAvatarSrc;
  const dirtyAvatar = draftAvatarKey !== avatarKey;

  function openAvatarEdit() {
    setDraftAvatarKey(avatarKey);
    setEditingAvatar(true);
  }

  function cancelAvatarEdit() {
    setDraftAvatarKey(avatarKey);
    setEditingAvatar(false);
  }

  async function saveAvatarEdit() {
    try {
      await api("/me/avatar", {
        method: "PUT",
        body: { avatarKey: draftAvatarKey },
        invalidatePrefixes: ["/me/profile", "/me/avatar"],
      });

      setAvatarKey(draftAvatarKey);

      if (typeof onUpdateProfile === "function") {
        await onUpdateProfile({ ...profile, avatarKey: draftAvatarKey });
      }

      setEditingAvatar(false);
    } catch (err) {
      console.error("Failed to save avatar", err);
      alert(t("profile.errors.saveAvatar"));
    }
  }

  async function saveLanguage(nextLang) {
    try {
      setLanguage(nextLang); // optimistic UI

      await api("/me/profile", {
        method: "PUT",
        body: { profile: { ...profile, language: nextLang } },
        invalidatePrefixes: ["/me/profile"],
      });

      if (typeof onUpdateProfile === "function") {
        await onUpdateProfile({ ...profile, language: nextLang });
      }
    } catch (err) {
      console.error("Failed to save language", err);
      setLanguage(profile.language || "en"); // rollback
      alert(t("profile.errors.saveLanguage"));
    }
  }

  // -------- Avatar edit mode --------
  if (editingAvatar) {
    return (
      <>
        <Navbar user={user} onLogout={onLogout} tab="profile" />
        <main className="page-wrapper">
          <section className="page-container">
            <div className="glass plan-glass profile-card">
              <div className="profile-header-row">
                <h2 className="profile-header-title">{t("profile.chooseAvatarTitle")}</h2>
              </div>

              <AvatarPicker value={draftAvatarKey} onChange={setDraftAvatarKey} />

              <div className="profile-avatar-actions">
                <button className="btn" type="button" onClick={cancelAvatarEdit}>
                  {t("common.cancel")}
                </button>

                <button
                  className="btn primary"
                  type="button"
                  onClick={saveAvatarEdit}
                  disabled={!dirtyAvatar}
                  title={
                    !dirtyAvatar
                      ? t("profile.saveAvatarDisabledTitle")
                      : t("profile.saveAvatarTitle")
                  }
                >
                  {t("common.save")}
                </button>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  // -------- Normal profile view --------
  return (
    <>
      <Navbar user={user} onLogout={onLogout} tab="profile" />
      <main className="page-wrapper">
        <section className="page-container">
          <div className="glass plan-glass profile-card">
            <div className="profile-header-row">
              <h2 className="profile-header-title">{t("profile.personalInfoTitle")}</h2>

              <div className="profile-avatar-wrap">
                <img
                  src={headerAvatarSrc}
                  alt={t("profile.avatarAlt")}
                  className="profile-header-avatar"
                />

                <button
                  type="button"
                  className="profile-avatar-edit"
                  onClick={openAvatarEdit}
                  aria-label={t("profile.editAvatarAria")}
                  title={t("profile.editAvatarTitle")}
                >
                  <FiEdit2 size={16} />
                </button>
              </div>
            </div>

            <div className="profile-personalinfo">
              <PersonalInfo profile={user?.profile} onSave={onUpdateProfile} />
            </div>

            <div className="profile-logout">
              <button className="btn danger" onClick={onLogout}>
                {t("profile.logout")}
              </button>
            </div>
          </div>

          <div className="glass plan-glass profile-card">
            <div>
              <LanguagePicker value={language} onChange={saveLanguage} disabled={false} />
            </div>
          </div>
        </section>
      </main>
    </>
  );
}